// src/components/subscription/BillingSettings.tsx
import React, { useState, useEffect } from 'react';
import { X, CreditCard, Calendar, Check, Crown, RotateCcw, ChevronsUpDown, Gift, ExternalLink } from 'lucide-react';
import { subscriptionApi, Subscription, UsageStats, SubscriptionHistory } from '../../services/subscriptionApi';
import UsageStatsBar from './UsageStatsBar';
import { PlanType } from './SubscriptionBadge';

interface BillingSettingsProps {
  onClose: () => void;
  subscription: Subscription | null;
  usageStats: UsageStats | null;
  onUpdate: () => void;
}

// Define Plan details structure
interface PlanDetails {
  id: PlanType;
  name: string;
  description: string;
  priceMonthly?: number;
  priceYearly?: number;
  features: string[];
  isCurrent?: boolean;
  iconColor?: string; // e.g., 'text-gray-600', 'text-blue-600', 'text-purple-600'
}

const BillingSettings: React.FC<BillingSettingsProps> = ({ 
  onClose, 
  subscription, 
  usageStats,
  onUpdate
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'plans' | 'payment' | 'history'>('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoSuccess, setPromoSuccess] = useState<string | null>(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState<SubscriptionHistory[]>([]);

  const isCanceled = subscription?.status === 'canceled';
  // Scheduled-to-cancel means Stripe has cancel_at_period_end=true but the
  // subscription is still active until currentPeriodEnd. The user keeps full
  // access during this window — the actual downgrade fires from the
  // customer.subscription.deleted webhook.
  const isCancelScheduled = !!subscription?.cancelAtPeriodEnd && !isCanceled;

  useEffect(() => {
    if (activeTab === 'history' && subscriptionHistory.length === 0) {
      fetchSubscriptionHistory();
    }
  }, [activeTab]);

  const fetchSubscriptionHistory = async () => {
    setIsHistoryLoading(true);
    try {
      const history = await subscriptionApi.getSubscriptionHistory();
      setSubscriptionHistory(history);
    } catch (error) {
      console.error('Failed to fetch subscription history:', error);
    } finally {
       setIsHistoryLoading(false);
    }
  };

  const handleUpgrade = async (planType: PlanType, paymentFrequency: 'monthly' | 'yearly') => {
    setIsLoading(true);
    try {
      const { url } = await subscriptionApi.createCheckoutSession(planType, paymentFrequency);
      window.location.href = url;
    } catch (error) {
      console.error('Failed to start upgrade process:', error);
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsLoading(true);
    try {
      const { url } = await subscriptionApi.createCustomerPortalSession();
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? Your access will continue until the end of the current billing period.')) return;
    setIsLoading(true);
    try {
      await subscriptionApi.cancelSubscription();
      onUpdate();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }
    setIsLoading(true);
    setPromoError(null);
    setPromoSuccess(null);
    
    // Add a timeout to prevent indefinite loading if the request doesn't respond
    const requestTimeout = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setPromoError('Request timed out. Please try again later.');
      }
    }, 15000); // 15-second timeout
    
    try {
      // First check if the promo code is valid
      const checkResult = await subscriptionApi.checkPromoCode(promoCode);
      if (!checkResult.valid) {
        setPromoError(checkResult.error || 'Invalid promo code');
        setIsLoading(false);
        clearTimeout(requestTimeout);
        return;
      }
      
      try {
        const result = await subscriptionApi.applyPromoCode(promoCode);
        clearTimeout(requestTimeout);
        
        if (result.success) {
          // If the promo code was successfully stored for later application
          if (result.error) {
            setPromoSuccess(result.error); // This is a special case where error contains a message about pending application
          } else {
            setPromoSuccess(`Promo code applied! Plan updated to ${result.subscription?.planType}.`);
          }
          setPromoCode('');
          onUpdate();
        } else {
          setPromoError(result.error || 'Failed to apply promo code.');
        }
      } catch (error: any) {
        console.error('Error applying promo code:', error);
        // Check for specific error messages related to active subscriptions or other conditions
        if (error.message && (
            error.message.includes('active subscription') || 
            error.message.includes('already have a subscription') ||
            error.message.includes('current subscription')
          )) {
          setPromoError('You already have an active subscription. Your promo code will be applied when your current subscription ends.');
        } else if (error.message && error.message.includes('pending promo code')) {
          setPromoError('You already have a pending promo code that will be applied when your current subscription ends.');
        } else if (error.message && error.message.includes('database')) {
          setPromoError('There was a problem with our database. Please try again later or contact support.');
        } else if (error.message && error.message.includes('already used')) {
          setPromoError('You have already used this promo code.');
        } else {
          setPromoError(error.message || 'Failed to apply promo code. Please try again later.');
        }
      }
    } catch (error: any) {
      clearTimeout(requestTimeout);
      console.error('Error checking promo code validity:', error);
      setPromoError('Error validating promo code. Please check your internet connection and try again.');
    } finally {
      clearTimeout(requestTimeout);
      setIsLoading(false);
    }
  };
  
  const renderOverviewTab = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <h3 className="text-base font-semibold mb-2 text-gray-800">Current Plan</h3>
            <div className="flex items-center gap-2 mb-1">
              {subscription?.planType === 'premium' ? <Crown size={18} className="text-purple-600" /> : subscription?.planType === 'medium' ? <Crown size={18} className="text-blue-600" /> : <Check size={18} className="text-gray-600" />}
              <span className="text-lg font-bold capitalize">{subscription?.planType || 'Free'}</span>
              {subscription?.isTrialing && (
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-medium">Trial</span>
              )}
              {subscription?.status === 'past_due' && (
                 <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full font-medium">Past Due</span>
              )}
              {isCanceled && (
                 <span className="bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded-full font-medium">Canceled</span>
              )}
              {isCancelScheduled && (
                 <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full font-medium">Cancels at period end</span>
              )}
            </div>

            <div className="text-sm text-gray-600 mb-4">
              {subscription?.status === 'trialing' && subscription.trialEndsAt && `Trial ends on ${new Date(subscription.trialEndsAt).toLocaleDateString()}.`}
              {subscription?.status === 'active' && !isCancelScheduled && subscription.currentPeriodEnd && `Renews on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}.`}
              {isCancelScheduled && subscription?.currentPeriodEnd && `Access ends on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}. You'll keep full access until then.`}
              {isCanceled && subscription?.currentPeriodEnd && `Access ended on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}.`}
            </div>
          </div>
          
          <div className="flex flex-col sm:items-end gap-2 w-full sm:w-auto">
            {subscription?.planType !== 'premium' && !isCanceled && !isCancelScheduled && (
              <button
                onClick={() => setActiveTab('plans')}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50"
                disabled={isLoading}
              >
                <Crown size={16} /> Upgrade Plan
              </button>
            )}
            {subscription?.stripeSubscriptionId && !isCanceled && (
              <>
                <button
                  onClick={handleManageSubscription}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
                  disabled={isLoading}
                >
                  <ExternalLink size={16} />
                  {isCancelScheduled ? 'Manage / Resume Subscription' : 'Manage Subscription'}
                </button>
                {!isCancelScheduled && (
                  <button
                    onClick={handleCancelSubscription}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-red-600 px-4 py-2 rounded-lg border border-gray-300 hover:bg-red-50 hover:border-red-300 transition-colors text-sm font-medium disabled:opacity-50"
                    disabled={isLoading}
                  >
                    <X size={16} /> Cancel Subscription
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {usageStats && (
        <UsageStatsBar
          aiQueries={usageStats.aiQueries}
          contacts={usageStats.contacts}
          todos={usageStats.todos}
          groups={usageStats.groups}
          plan={subscription?.planType || 'free'}
          showUpgradeInfo={true}
          nextTierLimits={usageStats.subscription.upgradedLimits ?? undefined}
        />
      )}
      
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-base font-semibold mb-3 text-gray-800 flex items-center">
          <Gift className="mr-2" size={18} /> Redeem Promo Code
        </h3>
        {promoError && <div className="text-red-600 text-sm mb-2">{promoError}</div>}
        {promoSuccess && (
          <div className={`text-sm mb-2 ${promoSuccess.includes('will be applied') ? 'text-blue-600' : 'text-green-600'}`}>
            {promoSuccess}
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Enter code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm disabled:opacity-50"
            disabled={isLoading}
          />
          <button
            onClick={handleApplyPromoCode}
            className="bg-white text-gray-700 px-4 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
            disabled={isLoading || !promoCode.trim()}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );

  const renderPlansTab = () => {
     // Determine the limits for the next tier if applicable
     const nextTierLimits = usageStats?.subscription.upgradedLimits;
     const currentPlanLimits = usageStats; // Assuming top-level usageStats has current limits

     const plans: PlanDetails[] = [
       {
         id: 'free',
         name: 'Free',
         description: 'Basic features for personal use',
         priceMonthly: 0,
         features: [
           // Use current limits from usageStats for the free plan display
           `${currentPlanLimits?.contacts?.limit ?? 5} contacts`,
           `${currentPlanLimits?.aiQueries?.limit ?? 10} AI queries / month`,
           'Basic features'
         ],
         isCurrent: subscription?.planType === 'free' || !subscription,
         iconColor: 'text-gray-600'
       },
       {
         id: 'medium',
         name: 'Medium',
         description: 'For growing personal networks',
         priceMonthly: 4.99,
         priceYearly: 49,
         features: [
           // Use nextTierLimits if the current plan is free, otherwise use current (medium) limits
           `${(subscription?.planType === 'free' ? nextTierLimits?.contacts : currentPlanLimits?.contacts?.limit) ?? 100} contacts`,
           `${(subscription?.planType === 'free' ? nextTierLimits?.aiQueries : currentPlanLimits?.aiQueries?.limit) ?? 50} AI queries / month`,
           'Email & calendar integration',
           'Advanced analytics'
         ],
         isCurrent: subscription?.planType === 'medium',
         iconColor: 'text-blue-600'
       },
       {
         id: 'premium',
         name: 'Premium',
         description: 'For professionals & power users',
         priceMonthly: 12.99,
         priceYearly: 120,
         features: [
           'Unlimited contacts',
           'Unlimited AI queries',
           'Priority support',
           'Early access to new features'
           // Limits for premium are usually displayed as "Unlimited" 
         ],
         isCurrent: subscription?.planType === 'premium',
         iconColor: 'text-purple-600'
       }
     ];

     return (
       <div className="space-y-6">
         <h2 className="text-xl font-bold text-gray-800">Choose Your Plan</h2>
         <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
           {plans.map(plan => (
             <div key={plan.id} className={`bg-white border rounded-lg flex flex-col ${plan.isCurrent ? 'border-brand-primary ring-2 ring-brand-primary/50' : 'border-gray-200'}`}>
               <div className="p-5 border-b border-gray-200">
                 <div className="flex items-center gap-2 mb-2">
                   <Crown size={18} className={plan.iconColor} />
                   <h3 className="text-lg font-semibold capitalize">{plan.name}</h3>
                 </div>
                 <p className="text-sm text-gray-600 mb-4 h-10">{plan.description}</p>
                 
                 {plan.priceMonthly !== undefined && plan.priceMonthly > 0 && (
                   <div className="flex items-baseline gap-4 mb-4">
                      <div className="flex-1 text-center">
                         <span className="text-3xl font-bold">${plan.priceMonthly}</span>
                         <span className="text-sm text-gray-500">/mo</span>
                      </div>
                       {plan.priceYearly && (
                         <div className="flex-1 text-center border-l border-gray-200 pl-4">
                            <span className="text-3xl font-bold">${plan.priceYearly}</span>
                            <span className="text-sm text-gray-500">/yr</span>
                         </div>
                       )}
                    </div>
                 )}
                 {plan.priceMonthly === 0 && (
                    <div className="mb-4">
                       <span className="text-3xl font-bold">$0</span>
                       <span className="text-sm text-gray-500 ml-1">Forever free</span>
                     </div>
                 )}

                 {/* Action Buttons */} 
                 {plan.isCurrent ? (
                   <button className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium cursor-default">Current Plan</button>
                 ) : plan.id === 'free' ? (
                   <button 
                     className="w-full bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
                     onClick={() => alert('Downgrading must be done via Stripe Customer Portal.')} // Or handle downgrade logic
                     disabled={isLoading}
                   >
                     Downgrade
                   </button>
                 ) : (
                   <div className="flex gap-2">
                      {plan.priceMonthly && (
                         <button
                           onClick={() => handleUpgrade(plan.id, 'monthly')}
                           className="flex-1 bg-brand-primary text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50"
                           disabled={isLoading}
                         >
                           Monthly
                         </button>
                      )}
                      {plan.priceYearly && (
                         <button
                           onClick={() => handleUpgrade(plan.id, 'yearly')}
                           className="flex-1 bg-white text-brand-primary px-3 py-2 rounded-lg border border-brand-primary hover:bg-indigo-50 transition-colors text-sm font-medium disabled:opacity-50"
                           disabled={isLoading}
                         >
                           Yearly
                         </button>
                      )}
                   </div>
                 )}
               </div>
               <div className="p-5 flex-grow">
                 <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">What's included:</h4>
                 <ul className="space-y-2">
                   {plan.features.map((feature, index) => (
                     <li key={index} className="flex items-start text-sm">
                       <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                       <span>{feature}</span>
                     </li>
                   ))}
                 </ul>
               </div>
             </div>
           ))}
         </div>
         <p className="text-center text-sm text-gray-500">
           Manage your payment details or cancel anytime via the Stripe Customer Portal (link available in Overview tab).
         </p>
       </div>
     );
   };

   const renderPaymentTab = () => (
     <div className="space-y-6">
       <h2 className="text-xl font-bold text-gray-800">Payment Method</h2>
       <div className="bg-white border border-gray-200 rounded-lg p-5">
         <p className="text-sm text-gray-600 mb-4">Manage your payment details, view invoices, and update your billing address through our secure payment partner, Stripe.</p>
         <button
           onClick={handleManageSubscription}
           className="flex items-center justify-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
           disabled={isLoading || !subscription?.stripeSubscriptionId}
         >
           <ExternalLink size={16} /> Go to Stripe Customer Portal
         </button>
         {!subscription?.stripeSubscriptionId && (
           <p className="text-xs text-gray-500 mt-2 italic">No active subscription found to manage.</p>
         )}
       </div>
     </div>
   );
  
  const renderHistoryTab = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Subscription History</h2>
      {isHistoryLoading ? (
          <div className="text-center py-10 text-gray-500">
             <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary mx-auto mb-3"></div>
             Loading history...
           </div>
      ) : subscriptionHistory.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <p className="text-sm text-gray-500 italic">No subscription history available.</p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {subscriptionHistory.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-2 whitespace-nowrap text-sm capitalize">{item.planType}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm capitalize">{item.status}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm">{new Date(item.startDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm">{item.endDate ? new Date(item.endDate).toLocaleDateString() : 'N/A'}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm capitalize">{item.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div 
      className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-out"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 sm:p-5 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">Billing & Subscription</h2>
          <button 
            onClick={onClose} 
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            disabled={isLoading}
            aria-label="Close billing settings"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 sm:px-6 border-b border-gray-200 flex-shrink-0">
           <nav className="-mb-px flex space-x-6" aria-label="Tabs">
             {([
               { key: 'overview', label: 'Overview' },
               { key: 'plans', label: 'Plans' },
               { key: 'payment', label: 'Payment' },
               { key: 'history', label: 'History' },
             ] as const).map((tab) => (
               <button
                 key={tab.key}
                 onClick={() => setActiveTab(tab.key)}
                 className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors duration-150 ${
                   activeTab === tab.key
                     ? 'border-brand-primary text-brand-primary'
                     : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                 }`}
                 aria-current={activeTab === tab.key ? 'page' : undefined}
               >
                 {tab.label}
               </button>
             ))}
           </nav>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto flex-grow bg-gray-50">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'plans' && renderPlansTab()}
          {activeTab === 'payment' && renderPaymentTab()}
          {activeTab === 'history' && renderHistoryTab()}
        </div>

        <div className="flex justify-end items-center gap-3 p-4 bg-gray-100 border-t border-gray-200 flex-shrink-0">
           <button
             type="button"
             onClick={onClose}
             className="bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
             disabled={isLoading}
           >
             Close
           </button>
         </div>
      </div>
    </div>
  );
};

export default BillingSettings;